import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../../utils/useDateFormat';
import { Wrench } from 'lucide-react';
import { VehicleService } from '../../../../../shared/types/vehicle-service.types';
import {
    getServiceHistoryItemStyles,
    getServiceHistoryTimelineLineStyles,
    serviceHistoryStyles,
    serviceHistoryWrenchColor,
} from './ServiceHistory.styles';

const serviceStatusConfig: Record<string, { color: 'warning' | 'success' | 'error' | 'info' | 'default' }> = {
    ordered: { color: 'info' },
    inProgress: { color: 'warning' },
    waitingForParts: { color: 'warning' },
    completed: { color: 'success' },
    cancelled: { color: 'default' },
};

interface ServiceHistoryProps {
    vin: string;
    services: VehicleService[];
    serviceTypeMap: Record<string, string>;
}

const ServiceHistory: React.FC<ServiceHistoryProps> = ({ vin, services, serviceTypeMap }) => {
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    if (services.length === 0) {
        return <Typography variant="body2" color="text.secondary">{t('vehicleDetail.serviceHistory.empty')}</Typography>;
    }

    return (
        <Box sx={serviceHistoryStyles.root}>
            {services.map((service, idx) => (
                <Box key={service.id} sx={getServiceHistoryItemStyles(idx < services.length - 1)}>
                    <Box sx={serviceHistoryStyles.timelineColumn}>
                        <Box sx={serviceHistoryStyles.timelineDot}>
                            <Wrench size={16} color={serviceHistoryWrenchColor} />
                        </Box>
                        {idx < services.length - 1 && (
                            <Box sx={getServiceHistoryTimelineLineStyles()} />
                        )}
                    </Box>
                    <Box
                        component={Link}
                        to={`/app/vehicles/${vin}/services/${service.id}`}
                        sx={serviceHistoryStyles.itemLink}
                    >
                        <Box sx={serviceHistoryStyles.itemHeader}>
                            <Typography variant="body2">
                                <Box component="span" color="text.secondary">{serviceTypeMap[service.serviceType] ?? service.serviceType} — </Box>
                                {service.description || '—'}
                            </Typography>
                            <Box sx={serviceHistoryStyles.itemMeta}>
                                <Chip
                                    label={t(`vehicleServiceStatus.${service.status}`, { defaultValue: service.status })}
                                    color={serviceStatusConfig[service.status]?.color ?? 'default'}
                                    size="small"
                                />
                                <Typography variant="body2" color="text.secondary" sx={serviceHistoryStyles.dateText}>
                                    {formatDate(service.createdAt)}
                                </Typography>
                            </Box>
                        </Box>
                        {(service.currentMileage !== undefined || service.currentHours !== undefined) && (
                            <Typography variant="caption" color="text.secondary">
                                {service.currentMileage !== undefined && `${service.currentMileage} km`}
                                {service.currentMileage !== undefined && service.currentHours !== undefined && ' · '}
                                {service.currentHours !== undefined && `${service.currentHours} mth`}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default ServiceHistory;
